package org.gymly.ui.admin

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.model.AdminClientAccountEntry
import org.gymly.model.AdminClientAccountEntryType
import org.gymly.model.AdminClientAccountSummary
import org.gymly.theme.AdminColors
import org.gymly.util.formatMoney

@Composable
fun ClientAccountsOverviewChart(
    summaries: List<AdminClientAccountSummary>,
    currency: String,
    modifier: Modifier = Modifier,
) {
    val credits = summaries
        .filter { it.balanceCents > 0 }
        .sortedByDescending { it.balanceCents }
    val totalCredit = credits.sumOf { it.balanceCents }

    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        Text("Resumen general", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = AdminColors.Text)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            OverviewKpi(
                "Total en monederos",
                formatMoney(totalCredit.toInt(), currency),
                AdminColors.Primary,
                Modifier.weight(1f),
            )
            OverviewKpi("Socios con saldo", "${credits.size}", AdminColors.Text, Modifier.weight(1f))
        }

        if (totalCredit > 0) {
            CreditTotalBar(totalCredit, currency)
        }

        if (credits.isNotEmpty()) {
            Text("Saldos a favor", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.TextMuted)
            val maxCredit = credits.maxOf { it.balanceCents }.toFloat().coerceAtLeast(1f)
            credits.forEach { client ->
                ClientCreditBarRow(
                    name = client.clientName,
                    creditCents = client.balanceCents,
                    maxCents = maxCredit,
                    currency = currency,
                )
            }
        }
    }
}

@Composable
fun ClientBalanceHistoryChart(
    entries: List<AdminClientAccountEntry>,
    currency: String,
    modifier: Modifier = Modifier,
) {
    val walletEntries = entries.walletMovementsOnly()
    val points = walletEntries
        .sortedBy { it.effectiveAt }
        .map { it.balanceAfterCents.toFloat() }
    if (points.size < 2) {
        Column(modifier.fillMaxWidth().padding(8.dp)) {
            Text("Evolución del saldo", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
            Spacer(Modifier.height(6.dp))
            Text(
                if (points.isEmpty()) "Sin movimientos en el monedero." else "Un solo abono registrado.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        return
    }

    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Text("Evolución del saldo", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
        Spacer(Modifier.height(8.dp))
        Canvas(
            Modifier
                .fillMaxWidth()
                .height(140.dp),
        ) {
            val minY = points.minOrNull() ?: 0f
            val maxY = points.maxOrNull() ?: 1f
            val range = (maxY - minY).coerceAtLeast(1f)
            val stepX = size.width / (points.size - 1).coerceAtLeast(1)
            val path = Path()
            points.forEachIndexed { index, value ->
                val x = index * stepX
                val y = size.height - ((value - minY) / range) * (size.height * 0.85f) - size.height * 0.05f
                if (index == 0) path.moveTo(x, y) else path.lineTo(x, y)
            }
            drawPath(path, AdminColors.Primary, style = Stroke(width = 3f))
            points.forEachIndexed { index, value ->
                val x = index * stepX
                val y = size.height - ((value - minY) / range) * (size.height * 0.85f) - size.height * 0.05f
                drawCircle(AdminColors.Primary, radius = 4f, center = Offset(x, y))
            }
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(
                "Mín ${formatMoney(points.minOrNull()!!.toInt(), currency)}",
                fontSize = 12.sp,
                color = AdminColors.TextMuted,
            )
            Text(
                "Máx ${formatMoney(points.maxOrNull()!!.toInt(), currency)}",
                fontSize = 12.sp,
                color = AdminColors.TextMuted,
            )
        }
    }
}

fun List<AdminClientAccountSummary>.filterWalletCredits(query: String): List<AdminClientAccountSummary> {
    val q = query.trim()
    return filter { summary ->
        summary.balanceCents > 0 && (
            q.isBlank() ||
                summary.clientName.contains(q, ignoreCase = true) ||
                (summary.clientEmail?.contains(q, ignoreCase = true) == true)
            )
    }
}

fun List<AdminClientAccountEntry>.walletMovementsOnly(): List<AdminClientAccountEntry> =
    filter {
        it.entryType == AdminClientAccountEntryType.CREDIT_FROM_OVERPAY ||
            it.entryType == AdminClientAccountEntryType.ADJUSTMENT ||
            it.entryType == AdminClientAccountEntryType.CREDIT_APPLY
    }

@Composable
private fun OverviewKpi(label: String, value: String, color: androidx.compose.ui.graphics.Color, modifier: Modifier = Modifier) {
    Column(modifier) {
        Text(label, fontSize = 12.sp, color = AdminColors.TextMuted)
        Text(value, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = color)
    }
}

@Composable
private fun CreditTotalBar(totalCredit: Long, currency: String) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
        Text("Total acumulado", Modifier.weight(0.35f), fontSize = 12.sp, color = AdminColors.TextMuted)
        Canvas(Modifier.weight(0.45f).height(18.dp)) {
            drawRoundRect(
                color = AdminColors.Primary.copy(alpha = 0.25f),
                size = Size(size.width, size.height),
                cornerRadius = CornerRadius(6f, 6f),
            )
            drawRoundRect(
                color = AdminColors.Primary,
                size = Size(size.width, size.height),
                cornerRadius = CornerRadius(6f, 6f),
            )
        }
        Text(
            formatMoney(totalCredit.toInt(), currency),
            Modifier.weight(0.2f),
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
            color = AdminColors.Primary,
        )
    }
}

@Composable
private fun ClientCreditBarRow(
    name: String,
    creditCents: Long,
    maxCents: Float,
    currency: String,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 3.dp),
        verticalAlignment = androidx.compose.ui.Alignment.CenterVertically,
    ) {
        Text(
            name,
            Modifier.weight(0.32f),
            fontSize = 12.sp,
            maxLines = 1,
            color = AdminColors.Text,
        )
        Canvas(Modifier.weight(0.48f).height(14.dp)) {
            drawRoundRect(
                color = AdminColors.Primary.copy(alpha = 0.15f),
                size = Size(size.width, size.height),
                cornerRadius = CornerRadius(3f, 3f),
            )
            val w = (creditCents / maxCents) * size.width
            if (w > 0f) {
                drawRoundRect(
                    AdminColors.Primary,
                    size = Size(w, size.height),
                    cornerRadius = CornerRadius(3f, 3f),
                )
            }
        }
        Text(
            formatMoney(creditCents.toInt(), currency),
            Modifier.weight(0.2f),
            fontSize = 10.sp,
            color = AdminColors.Primary,
        )
    }
}
